import { type CollectionLabel, DEFAULT_LOCALE, type Locale } from '@bailafm/shared';
import { ChevronDown, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { copyToClipboard } from '@/lib/clipboard';
import {
  collectionAvailableLocales,
  collectionUrlForLocale,
  LOCALE_LABELS,
} from '@/lib/public-urls';

/**
 * Botón copiar URL pública de una colección: una pastilla unificada con
 * dos zonas de clic. Clic izquierdo (Link2) → copia la URL en ES (o el
 * primer idioma con título); clic derecho (chevron) → dropdown con el
 * resto de idiomas con título. Se comparte entre el listado
 * (`CollectionsPage`) y el modal de crear/editar (`CollectionFormDialog`),
 * por eso recibe `slug` + `label` sueltos (el modal aún no tiene un
 * `CollectionAdminItem` al crear). Sin slug o sin ningún idioma con
 * título → no renderiza.
 */
export function CopyUrlButton({
  slug,
  label,
}: {
  slug: string;
  label: CollectionLabel;
}) {
  const available = collectionAvailableLocales(label);
  if (available.length === 0 || !slug) return null;

  const defaultLocale: Locale =
    available.find((l) => l === DEFAULT_LOCALE) ?? available[0];
  const others = available.filter((l) => l !== defaultLocale);

  async function handleCopy(locale: Locale) {
    const url = collectionUrlForLocale(slug, locale);
    try {
      await copyToClipboard(url);
      toast.success(`URL copiada (${LOCALE_LABELS[locale]})`);
    } catch {
      toast.error('No se pudo copiar la URL');
    }
  }

  return (
    <div
      className="inline-flex rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
      title="Copiar URL pública"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="rounded-r-none hover:bg-transparent"
        aria-label="Copiar URL pública"
        onClick={() => handleCopy(defaultLocale)}
      >
        <Link2 className="size-4" aria-hidden />
      </Button>
      {others.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="rounded-l-none w-5 px-0 hover:bg-transparent border-l border-transparent hover:border-border"
              aria-label="Copiar URL en otro idioma"
            >
              <ChevronDown className="size-3" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {others.map((l) => (
              <DropdownMenuItem key={l} onClick={() => handleCopy(l)}>
                <span>{LOCALE_LABELS[l]}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
